<template>
	<template v-if="data && callbackStillValid && !callbackAlreadyAnswered && !callbackSent">
		<TheForm :model="data" :rules="rules" ref="formRef" :waiting="waiting" :id="formId">
			<section>
				<h1>Bitte beantworten Sie folgende Rückfrage</h1>
				<p>
					Es gibt eine Rückfrage zu Ihrer Beschwerde mit dem Titel: <strong>"{{ data.complaint.title }}".</strong> Bitte beantworten Sie die Frage und
					helfen Sie uns damit Ihre Beschwerde schnellst- und bestmöglich zu bearbeiten.
				</p>

				<h2>Frage</h2>
				<p class="text">{{ data.text }}</p>
			</section>

			<DetailsMedia
				:files="complaintFiles.filter((f) => !f.isNew)"
				@addToFiles="null"
				@removeFromFiles="null"
				:showAddButton="false"
				:pageType="'rueckfrage'"
			/>
			<DetailsLinks
				:links="complaintLinks.filter((l) => !l.isNew)"
				@addToLinks="null"
				@removeFromLinks="null"
				:showAddButton="false"
				:pageType="'rueckfrage'"
			/>

			<section id="ihreAntwort">
				<h2 class="like-h1">Antwort geben</h2>
				<TheBox :title="'Ihre Antwort'" :titleLevel="3">
					<el-form-item label="Antworttext" prop="answer">
						<el-input v-model="data.answer" :autosize="{ minRows: 3 }" type="textarea" placeholder="Tragen Sie hier den Text Ihrer Antwort ein." />
					</el-form-item>
				</TheBox>
			</section>
			<section>
				<h2>Optionale Informationen</h2>
			</section>

			<DetailsMedia
				:files="complaintFiles.filter((f) => f.isNew)"
				@addToFiles="addToFiles"
				@removeFromFiles="removeFromFiles"
				:showAddButton="true"
				:pageType="'rueckfrage'"
			/>
			<DetailsLinks
				:links="complaintLinks.filter((l) => l.isNew)"
				@addToLinks="addToLinks"
				@removeFromLinks="removeFromLinks"
				:showAddButton="true"
				:pageType="'rueckfrage'"
			/>
			<section>
				<el-form-item prop="confirmation" label="">
					<el-checkbox v-model="data.confirmation">
						<strong>DSGVO und Mailzustimmung:</strong> {{ texts.werberat.dsgvo }}
						<RouterLink to="/datenschutz" title="Unsere Datenschutzbestimmungen" target="_blank" class="link">
							{{ texts.werberat.dsgvoLinktext }}
						</RouterLink>
					</el-checkbox>
				</el-form-item>

				<p><strong>Hinweis: </strong>{{ texts.werberat.dsgvoHinweis }}</p>
			</section>
			<section>
				<TheButton type="danger" @buttonclick="answerCallback"> Rückfrage beantworten </TheButton>
			</section>
		</TheForm>
	</template>

	<section v-else-if="data && !callbackStillValid">
		<h1>Rückmeldefrist abgelaufen</h1>
		<p> Leider ist die Frist für Ihre Beantwortung unserer Anfrage bereits abgelaufen. </p>
	</section>

	<section v-else-if="data && callbackAlreadyAnswered">
		<h1>Diese Rückfrage wurde schon beantwortet</h1>
		<p>Vielen Dank. Sie haben diese Rückfrage bereits beantwortet.</p>
	</section>

	<section v-else-if="data && callbackSent">
		<h1>Ihre Rückmeldung wurde eingereicht</h1>
		<p>Vielen Dank für Ihre Rückmeldung. </p>
	</section>

	<section v-else-if="loading === false">
		<el-alert :title="texts.general.emptyText" type="error" effect="dark" :closable="false" />
	</section>
</template>

<script lang="ts">
import { CallbackRequest, CallbackResponse, DetailFile, DetailLink, FileDto, LinkDto } from '@/types/types';
import { computed, defineComponent, ref, markRaw } from 'vue';
import { Loading } from '@element-plus/icons-vue';
import { startLoading, stopLoading } from '@/utils/loading/loadingHandler';
import { answerCallback, getCallback } from '@/requests/callbacks';
import { texts } from '@/utils/texts';
import TheForm from '@/components/TheForm.vue';
import DetailsLinks from './sections/DetailsLinks.vue';
import DetailsMedia from './sections/DetailsMedia.vue';
import TheBox from '@/components/TheBox.vue';
import TheButton from '@/components/TheButton.vue';
import { AnswerCallbackRequest } from '@/types/formdataTypes';
import { ElMessage, FormInstance } from 'element-plus';
import { useStore } from '@/store';
import { validateCheckbox, validateForm } from '@/utils/functions';

const ElMessageDuration = 3000;

interface AnswerCallbackResponseWithConfirmation extends CallbackResponse {
	confirmation?: boolean;
}

export default defineComponent({
	name: 'RueckfrageEdit',
	data() {
		return {
			data: null as AnswerCallbackResponseWithConfirmation | null,
			formId: 'callbackForm',
			loading: true,
			rules: {
				text: [
					{ required: true, message: texts.formErrors.reqMissing, trigger: ['blur'] },
					{ min: 3, message: texts.formErrors.minChars, trigger: 'blur' },
				],
				answer: [
					{ required: true, message: texts.formErrors.reqMissing, trigger: ['blur'] },
					{ min: 3, message: texts.formErrors.minChars, trigger: 'blur' },
				],
				confirmation: [{ validator: validateCheckbox, trigger: 'change' }],
			},
			waiting: false,
			complaintFiles: [] as DetailFile[],
			complaintLinks: [] as DetailLink[],
			callbackSent: false,
		};
	},
	components: {
		TheForm,
		DetailsMedia,
		DetailsLinks,
		TheBox,
		TheButton,
		Loading,
	},
	setup() {
		const formRef = ref<FormInstance>();
		const store = useStore();
		const loggedin = computed(() => store.state.loggedin);

		return {
			formRef,
			loggedin,
		};
	},
	props: {
		id: {
			type: String,
			required: true,
		},
	},
	computed: {
		token() {
			return this.$route.query.token as string;
		},
		texts() {
			return texts;
		},
		callbackStillValid() {
			if (!this.data) return false;
			const now = new Date();
			const dueDate = new Date(this.data.validToDate);

			return now < dueDate;
		},
		callbackAlreadyAnswered() {
			if (!this.data) return false;

			return this.data.answeredDate !== null;
		},
	},
	methods: {
		async loadCallback() {
			const loading = startLoading('#rueckfrage');

			const result = await getCallback(this.id, this.token);
			if (result.success) {
				this.data = result.data;
				//@ts-ignore
				this.data.confirmation = false;
				this.complaintLinks = this.data.complaint.links.map((l) => {
					return { link: l, isNew: false } as DetailLink;
				});

				this.complaintFiles = this.data.complaint.files.map((f) => {
					return { file: f, isNew: false } as DetailFile;
				});
			}

			this.loading = false;
			stopLoading(loading);
		},
		async answerCallback() {
			if (!this.data) return;

			const isValid = await validateForm(this.formRef, this.formId);
			if (!isValid) return;

			this.waiting = true;
			const waitingMsg = ElMessage({ message: texts.formErrors.waiting, type: 'info', duration: 0, showClose: false, icon: markRaw(Loading) });

			const answerCallbackRequest = {
				callbackId: this.data.id,
				emailToken: this.token,
				answer: this.data.answer,
			} as AnswerCallbackRequest;

			const requestFiles = this.complaintFiles.filter((f) => f.isNew).map((f) => f.file);
			if (requestFiles.length > 0) {
				answerCallbackRequest.files = requestFiles.map((file) => {
					return {
						fileName: file.fileName,
						bytes: file.bytes,
						contentType: file.contentType,
					} as FileDto;
				});
			}

			const requestLinks = this.complaintLinks.filter((l) => l.isNew).map((l) => l.link);
			if (requestLinks.length > 0) {
				answerCallbackRequest.links = requestLinks.map((link) => {
					return {
						name: link.name,
						url: link.url,
						type: link.type,
					} as LinkDto;
				});
			}

			const answerCallbackResult = await answerCallback(answerCallbackRequest);
			if (answerCallbackResult.success) {
				this.callbackSent = true;
			} else {
				ElMessage({ message: texts.callbacks.saveFailed, type: 'error', duration: ElMessageDuration });
			}

			this.waiting = false;
			waitingMsg.close();
		},
		addToFiles(file) {
			this.complaintFiles.push(file);
		},
		removeFromFiles(file) {
			if (!this.data) return;
			this.complaintFiles = this.complaintFiles.filter((f) => f.file !== file.file);
		},
		removeFromLinks(link) {
			if (!this.data) return;
			this.complaintLinks = this.complaintLinks.filter((l) => l.link !== link.link);
		},
		addToLinks(linkObj) {
			this.complaintLinks.push(linkObj);
		},
	},
	mounted() {
		this.loadCallback();
	},
});
</script>

<style lang="scss">
#rueckfrage-bearbeiten {
	.text {
		display: inline-block;
		margin: 0;
	}

	section:first-child {
		margin-top: auto;
	}

	#ihreAntwort {
		h3 {
			font-size: 1.75rem;
			font-weight: 500;
		}
	}
}
</style>
